import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef
} from "react";
import { Tooltip } from "react-tooltip";
import { useDispatch, useSelector } from "react-redux";
import { getUserKPIReportData } from "../../../../../redux/userReportsSlice";
import {
  monthlyKPIReportsData,
  noDataPresent
} from "../../../../../locales/en";
import { useVisibleColumnCount } from "../../../../../hooks/useVisibleColumnCount";
import MonthlyKpiReportFilter from "../../../../../pages/reports/kpi-report/MonthlyKpiReportFilter";
import MonthlyKpiReportInvoiceModal from "../../../../modals/MonthlyKpiReportInvoiceModal";
import MonthlyKpiReportCustomerModal from "../../../../modals/MonthlyKpiReportCustomerModal";
import MonthlyKpiGraphReportModal from "../../../../modals/MonthlyKpiGraphReportModal";
import MonthlyKpiReportReferralModal from "../../../../modals/MonthlyKpiReportReferralModal";
import TableSkeleton from "../../../../skeletons/TableSkeleton";
import "./../../reportable/reportable.scss";
import DualPagination from "../../../../common/DualPagination";
import {
  ALL,
  CUSTOMERTYPE,
  INVOICE,
  MONTH,
  MONTH_12,
  REFERRAL
} from "../../../../../utils/constants/appConstants";
import ErrorBoundary from "../../../../error-boundary";
import ListViewFallback from "../../../../error-boundary/fallback/ListViewFallback";
import "./../../../../../assets/scss/common.scss";

const MonthlyKpiReportTable = () => {
  const dispatch = useDispatch();
  const invoiceIdsListRef = useRef(null);
  const loading = useSelector((state) => state.userReport.kpiReportDataLoader);
  const { headerRef, visibleColumnCount } = useVisibleColumnCount();
  const [data, setData] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [addInvDtlsModal, setAddInvDtlsModal] = useState(false);
  const [addcustmrModal, setAddcustmrModal] = useState(false);
  const [addReferralModal, setAddReferralModal] = useState(false);
  const [addgrapgModal, setAddgrapgModal] = useState(false);
  const initialValues = {
    viewType: MONTH,
    period: MONTH_12,
    countrySelect: ALL
  };
  const [values, setValues] = useState(initialValues);
  const [currentFilterType, setCurrentFilterType] = useState(null);
  const toggleAddInvDtlsModal = useCallback(() => {
    setAddInvDtlsModal((v) => !v);
  }, []);
  const toggleAddcustmrModal = useCallback(() => {
    setAddcustmrModal((v) => !v);
  }, []);
  const toggleAddgrapgModal = useCallback(() => {
    setAddgrapgModal((v) => !v);
  }, []);
  const toggleAddReferralmrModal = useCallback(() => {
    setAddReferralModal((v) => !v);
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const paramsData = {
          viewType: values.viewType,
          periods: values.period,
          country: values.countrySelect,
          page: pageIndex,
          size: pageSize
        };
        const response = await dispatch(
          getUserKPIReportData(paramsData)
        ).unwrap();
        setData(response?.data || []);
        setTotalPages(response?.pageInfo?.totalPages || 0);
      } catch {
        //handle error
      }
    };
    loadUsers();
  }, [
    values.viewType,
    values.period,
    values.countrySelect,
    pageIndex,
    pageSize,
    dispatch
  ]);

  const headers = useMemo(() => data[0] || [], [data]);
  const bodyRows = useMemo(() => data.slice(1), [data]);

  const handleUserModal = (rowData) => {
    invoiceIdsListRef.current = rowData.breakdownData?.ids;
    setAddcustmrModal(true);
    setCurrentFilterType(rowData.breakdownData?.type);
  };

  const handleInvoiceModal = (rowData) => {
    invoiceIdsListRef.current = rowData.breakdownData?.ids;
    setAddInvDtlsModal(true);
    setCurrentFilterType(rowData.breakdownData?.type);
  };

  const handleReferralModal = (rowData) => {
    invoiceIdsListRef.current = rowData.breakdownData?.ids;
    setAddReferralModal(true);
    setCurrentFilterType(rowData.breakdownData?.type);
  };

  // Which breakdown modal a clickable cell opens depends on its breakdown type
  const getBreakdownClickHandler = (cell) => {
    const breakdownType = cell.breakdownData?.type;

    if (breakdownType === CUSTOMERTYPE) {
      return () => handleUserModal(cell);
    }
    if (breakdownType === INVOICE) {
      return () => handleInvoiceModal(cell);
    }
    if (breakdownType === REFERRAL) {
      return () => handleReferralModal(cell);
    }
    return undefined;
  };

  const tooltipConfig = useMemo(
    () => ({
      [`${monthlyKPIReportsData?.signedUp}`]: `${monthlyKPIReportsData?.toolTip?.data1}`,
      [`${monthlyKPIReportsData?.linkedAccounting}`]: `${monthlyKPIReportsData?.toolTip?.data2}`,
      [`${monthlyKPIReportsData?.submittedInvoice}`]: `${monthlyKPIReportsData?.toolTip?.data3}`,
      [`${monthlyKPIReportsData?.preapproved}`]: `${monthlyKPIReportsData?.toolTip?.data4}`,
      [`${monthlyKPIReportsData?.NZFunded}`]: `${monthlyKPIReportsData?.toolTip?.data5}`,
      [`${monthlyKPIReportsData?.AUFunded}`]: `${monthlyKPIReportsData?.toolTip?.data6}`,
      [`${monthlyKPIReportsData?.NZShare}`]: `${monthlyKPIReportsData?.toolTip?.data7}`,
      [`${monthlyKPIReportsData?.AUShare}`]: `${monthlyKPIReportsData?.toolTip?.data8}`,
      [`${monthlyKPIReportsData?.lending}`]: `${monthlyKPIReportsData?.toolTip?.data9}`,
      [`${monthlyKPIReportsData?.lendingCustomer}`]: `${monthlyKPIReportsData?.toolTip?.data18}`,
      [`${monthlyKPIReportsData?.newClients}`]: `${monthlyKPIReportsData?.toolTip?.data10}`,
      [`${monthlyKPIReportsData?.partnerReferral}`]: `${monthlyKPIReportsData?.toolTip?.data11}`,
      [`${monthlyKPIReportsData?.customers}`]: `${monthlyKPIReportsData?.toolTip?.data12}`,
      [`${monthlyKPIReportsData?.new}`]: `${monthlyKPIReportsData?.toolTip?.data13}`,
      [`${monthlyKPIReportsData?.preapprovedFunded}`]: `${monthlyKPIReportsData?.toolTip?.data14}`,
      [`${monthlyKPIReportsData?.returning}`]: `${monthlyKPIReportsData?.toolTip?.data15}`,
      [`${monthlyKPIReportsData?.notTransacted_Approved}`]: `${monthlyKPIReportsData?.toolTip?.data16}`
    }),
    [monthlyKPIReportsData]
  );

  const highlightRows = useMemo(
    () => [
      monthlyKPIReportsData?.newLeads,
      monthlyKPIReportsData?.customersFunded,
      monthlyKPIReportsData?.lending,
      monthlyKPIReportsData?.lendingCustomer,
      monthlyKPIReportsData?.futureRevenue,
      monthlyKPIReportsData?.collectedRevenue,
      monthlyKPIReportsData?.referrals,
      monthlyKPIReportsData?.countries
    ],
    [monthlyKPIReportsData]
  );

  const clickableRows = useMemo(
    () => [
      monthlyKPIReportsData?.preapproved,
      monthlyKPIReportsData?.NZFunded,
      monthlyKPIReportsData?.AUFunded,
      monthlyKPIReportsData?.NZShare,
      monthlyKPIReportsData?.AUShare,
      monthlyKPIReportsData?.signedUp,
      monthlyKPIReportsData?.newClients,
      monthlyKPIReportsData?.partnerReferral,
      monthlyKPIReportsData?.new,
      monthlyKPIReportsData?.preapprovedFunded,
      monthlyKPIReportsData?.revenue,
      monthlyKPIReportsData?.revenueCustomer
    ],
    [monthlyKPIReportsData]
  );

  const formatPercent = useCallback((num) => {
    if (num === 0) return "0";
    return num.toFixed(1);
  }, []);

  const commonModalProps = {
    filterType: currentFilterType,
    invoiceIdsListRef
  };

  return (
    <>
      <div className="col-md-12 d-flex flex-wrap mt-3 mb-3 justify-content-between search">
        <MonthlyKpiReportFilter
          values={values}
          setValues={setValues}
          loading={loading}
        />
      </div>
      <div className="col-md-12">
        <div className="table-responsive">
          <ErrorBoundary fallback={<ListViewFallback />}>
            <table
              className={`mt-2 mb-3 overduetable monthly-kpi-head bgless_table`}
            >
              <thead ref={headerRef}>
                <tr>
                  {headers.map((col, idx) => (
                    <th key={idx}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <TableSkeleton
                    rows={10}
                    columns={
                      headers.length === 0
                        ? visibleColumnCount || 10
                        : headers.length
                    }
                  />
                )}
                {!loading && bodyRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={
                        headers.length === 0
                          ? visibleColumnCount || 10
                          : headers.length
                      }
                      className="text-center py-3"
                    >
                      {noDataPresent}
                    </td>
                  </tr>
                )}
                {!loading &&
                  bodyRows.length > 0 &&
                  bodyRows.map((row, rowIndex) => {
                    const firstCell = row[0];
                    const hasData = row
                      .slice(1)
                      .some((cell) => cell && cell !== 0 && cell !== "");
                    const allBlankAfterName = row
                      .slice(1)
                      .every((cell) => !cell);

                    const rowPrimaryClass =
                      highlightRows.includes(firstCell) && allBlankAfterName
                        ? "bg-primary text-white"
                        : "";

                    return (
                      <tr key={rowIndex} className={rowPrimaryClass}>
                        {row.map((cell, colIndex) => {
                          if (colIndex === 0) {
                            const tooltipContent =
                              tooltipConfig[firstCell] && hasData
                                ? tooltipConfig[firstCell]
                                : null;
                            return (
                              <td key={colIndex}>
                                <span
                                  data-tooltip-id={`tooltip-${rowIndex}`}
                                  data-tooltip-content={tooltipContent}
                                  className={
                                    tooltipContent
                                      ? "cursor-pointer d-block"
                                      : ""
                                  }
                                >
                                  {firstCell}
                                </span>
                                {tooltipContent && (
                                  <Tooltip
                                    id={`tooltip-${rowIndex}`}
                                    place="top"
                                    className="kpiTooltip"
                                  />
                                )}
                              </td>
                            );
                          }

                          if (!cell || typeof cell !== "object") {
                            return <td key={colIndex}>{cell || ""}</td>;
                          }

                          const value = cell.value ?? "";

                          const percentArray =
                            cell.breakdownData?.percent || [];
                          const firstPercent =
                            percentArray[0] !== undefined &&
                            percentArray[0] !== null
                              ? Number(percentArray[0])
                              : null;

                          const secondPercent =
                            percentArray[1] !== undefined &&
                            percentArray[1] !== null
                              ? Number(percentArray[1])
                              : null;

                          const displayValue =
                            firstPercent !== null
                              ? `${value} (${formatPercent(firstPercent)}%)` +
                                (secondPercent !== null
                                  ? `[${formatPercent(secondPercent)}%]`
                                  : "")
                              : value;

                          const rawValueStr = String(value);
                          const numericFromValue = parseFloat(
                            rawValueStr.replace(/[^0-9.-]/g, "")
                          );
                          const isNumericZero =
                            !isNaN(numericFromValue) && numericFromValue === 0;
                          const isPercentZero =
                            firstPercent !== null &&
                            firstPercent === 0 &&
                            (secondPercent !== null
                              ? secondPercent === 0
                              : true);
                          const isZeroLike = isNumericZero || isPercentZero;
                          const prevRowLabel =
                            rowIndex > 0 ? data[rowIndex - 1][0] : null;
                          const shouldAllowClick =
                            clickableRows.includes(firstCell) &&
                            !isZeroLike &&
                            (firstCell !== monthlyKPIReportsData?.signedUp ||
                              prevRowLabel ===
                                monthlyKPIReportsData?.referrals);

                          const clickHandler = shouldAllowClick
                            ? getBreakdownClickHandler(cell)
                            : undefined;
                          const tdClass = shouldAllowClick
                            ? "cursor-pointer primary-color"
                            : "";

                          return (
                            <td
                              key={colIndex}
                              className={tdClass}
                              onClick={clickHandler}
                            >
                              {displayValue}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </ErrorBoundary>
        </div>
      </div>

      {data && data.length > 0 && totalPages > 0 && (
        <DualPagination
          gotoPage={(p) => setPageIndex(p)}
          previousPage={() => setPageIndex((p) => Math.max(0, p - 1))}
          nextPage={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
          canPreviousPage={pageIndex > 0}
          canNextPage={pageIndex < totalPages - 1}
          pageIndex={pageIndex}
          pageCount={totalPages}
          pageOptions={Array.from({ length: totalPages }, (_, i) => i)}
          setPageSize={(s) => {
            setPageSize(s);
            setPageIndex(0);
          }}
          pageSize={pageSize}
          dataLength={data.length}
        />
      )}

      <MonthlyKpiReportInvoiceModal
        isOpen={addInvDtlsModal}
        toggle={toggleAddInvDtlsModal}
        {...commonModalProps}
      />

      <MonthlyKpiReportCustomerModal
        isOpen={addcustmrModal}
        toggle={toggleAddcustmrModal}
        {...commonModalProps}
      />

      <MonthlyKpiReportReferralModal
        isOpen={addReferralModal}
        toggle={toggleAddReferralmrModal}
        {...commonModalProps}
      />

      <MonthlyKpiGraphReportModal
        isOpen={addgrapgModal}
        toggle={toggleAddgrapgModal}
      />
    </>
  );
};
export default React.memo(MonthlyKpiReportTable);
